import React from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import analytics from '../../../modules/analytics';
import {
  isAnonymous,
  ShareMessage,
  ShareUser,
  ShareChannel,
  AuthenticatedShareUser,
} from '../../../modules/share';
import { WhoAmI } from '../../../store/CurrentUser.types';
import type { RootState } from '../../../store/models';
import { ShareProps } from '../sharing';

type SharePropsWithoutUser = Omit<ShareProps, 'user' | 'message'> & {
  location: string;
  message?: ShareMessage | ((user: ShareUser, trackingId: any) => ShareMessage);
};

type Callbacks = {
  onShareTrigger?: () => void;
  onShareSelect?: (channel?: ShareChannel) => void;
  onShareComplete?: (channel?: ShareChannel) => void;
  onShareAbort?: (err?: Error) => void;
};

/**
 * Wraps callbacks within shareProps with tracking calls and returns the wrapped functions.
 * @param user The current user
 * @param shareProps The sharing configuration
 * @param params Any additional tracking params
 * @returns Share callbacks wrapped with tracking calls
 */
const makeReferralTrackers = (
  user: ShareUser,
  shareProps: SharePropsWithoutUser,
  params?: Record<string, any>,
): Callbacks => {
  let userId: number | 'guest' = 'guest';

  if (!isAnonymous(user)) {
    userId = user.user_id;
  }

  const args = {
    target: shareProps.target,
    user_id: userId,
    provider_id: shareProps.provider?.id,
    ...params,
  };

  return {
    onShareTrigger: () => {
      analytics.track(`${shareProps.location}_referral_clicked`, args);

      if (shareProps.onShareTrigger) shareProps.onShareTrigger();
    },
    onShareSelect: (channel?: ShareChannel) => {
      analytics.track(`${shareProps.location}_referral_selected`, {
        ...args,
        channel,
      });

      if (shareProps.onShareSelect) shareProps.onShareSelect(channel);
    },
    onShareComplete: (channel?: ShareChannel) => {
      analytics.track(`${shareProps.location}_referral_completed`, {
        ...args,
        channel,
      });

      if (shareProps.onShareComplete) shareProps.onShareComplete(channel);
    },
    onShareAbort: (err?: Error) => {
      if (err) {
        analytics.track(`${shareProps.location}_referral_error`, args);
      } else {
        analytics.track(`${shareProps.location}_referral_aborted`, args);
      }

      if (shareProps.onShareAbort) shareProps.onShareAbort(err);
    },
  };
};

/**
 * Given some starter props, generates a full set of props to send to `ShareTrigger`, including
 * wrapping share callbacks with tracking.
 * @param shareConfig The sharing configuration
 * @returns Props that can be sent to a `ShareTrigger` instance, or null, if not in the test
 */
export function useReferralEntrypointProps(
  shareConfig: SharePropsWithoutUser,
): ShareProps {
  // compute the props to supply
  const shareProps: ShareProps = useSelector<RootState, ShareProps>(state => ({
    ...shareConfig,
    message: (
      typeof shareConfig.message === 'function'
        ? shareConfig.message(state.user as WhoAmI, state.trackingId?.trackingId)
        : shareConfig.message
    ),
    user: state.user as WhoAmI,
    // inject tracking into referral / share callbacks
    ...makeReferralTrackers(
      state.user as WhoAmI,
      shareConfig,
    ),
  }), (left, right) => {
    // make sure there's an actual difference between the objects being calculated by the selector
    const leftUserId: number | 'guest' = isAnonymous(left.user) ? 'guest' : left.user.user_id;
    const rightUserId: number | 'guest' = isAnonymous(right.user) ? 'guest' : right.user.user_id;

    return (
      leftUserId === rightUserId
      && isEqual(left.message, right.message)
      && left.provider?.id === right.provider?.id
    );
  });

  const [props, setProps] = React.useState(null);
  React.useEffect(() => {
    setProps(shareProps);
  }, [
    (shareProps?.user as AuthenticatedShareUser)?.user_id,
    shareProps?.message?.urlParams?.[2]?.value,
  ]);

  return props;
}
